import { useQuery } from "@tanstack/react-query";
import type { Address } from "viem";
import type { RedKeel } from "../config/networks";

/**
 * QUÉ ESTÁ HACIENDO EL ROBOT CON ESTA PISCINA, Y POR QUÉ.
 *
 * Una piscina fuera de rango y un robot que no hace nada se ven igual estén bien o estén rotos. Y la
 * diferencia importa: si el robot está esperando a propósito —a que el precio se aleje del borde, a
 * que el precio medio confirme la salida, a que el dueño recargue la bolsa de gas…— el dueño tiene
 * que poder leerlo, no quedarse mirando un "reajustando" que no es verdad.
 *
 * El keeper publica en cada ronda el motivo de cada vault (`/keeper.<red>.json`, mismo origen, sin
 * secretos: todo sale de la cadena). Aquí se lee y se enseña.
 *
 * SI EL FICHERO SE QUEDA VIEJO, NO SE CUENTA NADA. Un keeper parado dejaría su último estado
 * congelado, y enseñarlo sería peor que no decir nada. Por eso se descarta pasados 15 minutos.
 */
export type MotivoRobot =
  | "en_rango" | "cooldown" | "sin_gas" | "margen_recuperacion" | "twap" | "tick_multiplo"
  | "gas" | "gas_caro" | "sin_cuota" | "pausado" | "sin_posicion" | "otro_operador"
  | "reintento" | "reajustando" | "reajustado" | "cerrando" | "otro";

export type EsperaKeeper = {
  motivo: MotivoRobot | string;
  /** desde cuándo está en este motivo (unix s) */
  desde?: number;
  detalle?: string;
  // Solo con motivo "gas" (espera por base fee alto):
  /** base fee actual de la red, en gwei */
  gwei?: number;
  /** por debajo de esto reajusta */
  umbral?: number;
  /** cuánto lleva esperando, en segundos */
  esperando_s?: number;
  /** cuánto puede esperar como mucho antes de reajustar igual */
  tope_s?: number;
  /** Capital parado (añadido o reinvertido que aún no entró en la posición) y qué hace el robot
   *  con él. Solo aparece cuando el keeper lo vio en esta ronda. */
  barrido?: BarridoKeeper;
  // De la ronda (comunes a toda la red):
  /** cuándo publicó el keeper (unix s) */
  actualizado: number;
  /** cuándo empieza la siguiente revisión (unix s), si se sabe */
  proxima?: number | null;
  /** cada cuánto revisa el robot (s) */
  ronda?: number | null;
};

export type BarridoKeeper = {
  /** bajo_minimo | fuera_rango | sin_gas | espera | barriendo | parcial | colocado | con_reajuste | reintento */
  estado: string;
  /** lo pendiente según el robot, en el token estable */
  pendiente: number;
  /** cuándo lo vuelve a intentar (unix s); null = en la siguiente ronda */
  siguiente?: number | null;
  detalle?: string;
  /** por debajo de esto no compensa barrer (gas > lo que se coloca) */
  minimo?: number;
};

type FicheroKeeper = {
  actualizado: number;
  nextAt?: number | null;
  cycleSeconds?: number | null;
  pollSeconds?: number | null;
  vaults?: Record<string, Omit<EsperaKeeper, "actualizado" | "proxima" | "ronda">>;
};

const FRESCO_MS = 15 * 60 * 1000;

export function useEsperaKeeper(red: RedKeel, vault?: Address) {
  return useQuery({
    queryKey: ["espera-keeper", red.clave, vault],
    enabled: !!vault,
    staleTime: 45_000,
    refetchInterval: 60_000,
    queryFn: async (): Promise<EsperaKeeper | null> => {
      try {
        // Mismo fichero para todos los vaults de la red: la URL cambia cada 30 s para que el
        // navegador lo cachee entre fichas y no se pida en cada render.
        const r = await fetch(`/keeper.${red.clave}.json?t=${Math.floor(Date.now() / 30_000)}`);
        if (!r.ok) return null; // la red no publica estado: se usa el texto genérico
        const d = (await r.json()) as FicheroKeeper;
        if (!d?.actualizado || Date.now() - d.actualizado * 1000 > FRESCO_MS) return null;
        const e = d.vaults?.[vault!.toLowerCase()];
        if (!e) return null;
        return { ...e, actualizado: d.actualizado, proxima: d.nextAt ?? null, ronda: d.pollSeconds ?? null };
      } catch {
        // Sin fichero, nginx sirve index.html (no es JSON): igual que si no existiera.
        return null;
      }
    },
  });
}
